// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Fixed composer (FR-11): disabled plus affordance, auto-growing textarea, and
// send button. Enter sends, Shift+Enter inserts a newline, whitespace-only
// input cannot be sent.

import { useLayoutEffect, useRef, useState } from 'react';
import { ELIA_COLORS, ELIA_FONT } from './elia-theme';
import { isSendableText } from './elia-chat-reducer';
import { EliaPlusIcon, EliaSendIcon } from './EliaIcons';

const MAX_TEXTAREA_HEIGHT = 120;

export function EliaComposer({
  onSend,
  busy = false,
  placeholder,
}: {
  onSend: (text: string) => void;
  busy?: boolean;
  placeholder: string;
}) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const canSend = isSendableText(value) && !busy;

  // Auto-grow the textarea up to a capped height.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [value]);

  const submit = () => {
    if (!canSend) return;
    onSend(value);
    setValue('');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      style={{
        borderTop: `1px solid ${ELIA_COLORS.borderSubtle}`,
        padding: '13px 12px 12px',
        fontFamily: ELIA_FONT,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
        <button
          type="button"
          aria-label="Add attachment (coming soon)"
          disabled
          style={{
            width: 32,
            height: 32,
            borderRadius: '9999px',
            border: 'none',
            background: 'transparent',
            color: ELIA_COLORS.textFaint,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'not-allowed',
            flexShrink: 0,
            padding: 0,
          }}
        >
          <EliaPlusIcon size={20} />
        </button>

        <textarea
          ref={textareaRef}
          value={value}
          rows={1}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label="Message Elia AI"
          style={{
            flex: 1,
            minWidth: 0,
            resize: 'none',
            border: 'none',
            outline: 'none',
            background: ELIA_COLORS.neutralSurface,
            borderRadius: 20,
            padding: '10px 16px',
            fontFamily: ELIA_FONT,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '-0.15px',
            color: ELIA_COLORS.text,
            maxHeight: MAX_TEXTAREA_HEIGHT,
            overflowY: 'auto',
          }}
        />

        <button
          type="button"
          aria-label="Send message"
          onClick={submit}
          disabled={!canSend}
          style={{
            width: 36,
            height: 36,
            borderRadius: '9999px',
            border: 'none',
            background: ELIA_COLORS.pink,
            opacity: canSend ? 1 : 0.4,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: canSend ? 'pointer' : 'default',
            flexShrink: 0,
            padding: 0,
          }}
        >
          <EliaSendIcon size={16} color="#ffffff" />
        </button>
      </div>
    </div>
  );
}
